import Vue from 'vue'
import LoginService from '../service/login'
import {
    decidir,
    expiracaoDoToken,
    AVISO_ANTES_MS,
    LIMITE_OCIOSIDADE_MS
} from '../utils/sessaoRegras'

//
// Runtime do controle de sessao. As REGRAS estao em utils/sessaoRegras.js;
// aqui ficam so os efeitos: relogio, escuta de eventos, localStorage e
// redirecionamento.
//
// Mora num plugin iniciado no main.js, e nao no Header.vue como antes. O
// Header e componente de apresentacao, montado condicionalmente pelo App.vue -
// controle de sessao dentro dele reinicia a cada remontagem e some nas rotas
// em que o cabecalho nao aparece.
//

export const CHAVE_ULTIMA_ATIVIDADE = 'sessaoUltimaAtividade'
export const CHAVE_INICIO = 'sessaoInicio'
export const CHAVE_MOTIVO_ENCERRAMENTO = 'sessaoMotivoEncerramento'

/**
 * Eventos que contam como atividade. Sao interacoes DELIBERADAS do usuario.
 *
 * Nao entram aqui: requisicao de rede (o polling de faturas sozinho manteria a
 * sessao viva para sempre - e exatamente o problema que estamos consertando) e
 * mousemove puro (o ponteiro encostado numa mesa que treme renovaria sessao).
 */
const EVENTOS_DE_ATIVIDADE = ['mousedown', 'keydown', 'touchstart', 'wheel']

/** Grava no maximo uma vez a cada 10s - senao seria escrita a cada tecla. */
const INTERVALO_MINIMO_REGISTRO_MS = 10 * 1000

const INTERVALO_TICK_MS = 1000

/**
 * Estado observavel consumido pela UI: o dialogo de aviso no App.vue e o
 * contador no Header.vue.
 */
export const estadoSessao = Vue.observable({
    avisando: false,
    motivo: null,
    segundos: 0,
    restanteOciosidadeMs: LIMITE_OCIOSIDADE_MS
})

let intervalo = null
let ultimoRegistro = 0
let renovacaoEmVoo = false

function ler(chave) {
    try {
        const valor = localStorage.getItem(chave)
        return valor ? parseInt(valor, 10) : null
    } catch (e) {
        return null
    }
}

function gravar(chave, valor) {
    try {
        localStorage.setItem(chave, String(valor))
    } catch (e) {
        // localStorage indisponivel - a sessao passa a valer so nesta aba.
    }
}

function temSessao() {
    try {
        return !!localStorage.getItem('token')
    } catch (e) {
        return false
    }
}

/**
 * Marca atividade. O timestamp vai para o localStorage de proposito: e o que
 * faz uma aba ativa segurar a sessao das outras, e o que faz a ociosidade
 * continuar contando com a aba fechada (decisao de produto: fechou e voltou
 * depois de 15 min, entra deslogado).
 */
export function registrarAtividade(agora) {
    const instante = agora || Date.now()
    if (instante - ultimoRegistro < INTERVALO_MINIMO_REGISTRO_MS) return false

    ultimoRegistro = instante
    gravar(CHAVE_ULTIMA_ATIVIDADE, instante)
    return true
}

/** Chamado pelo Login: zera os tres relogios. */
export function iniciarContagemDaSessao(agora) {
    const instante = agora || Date.now()
    ultimoRegistro = instante
    gravar(CHAVE_INICIO, instante)
    gravar(CHAVE_ULTIMA_ATIVIDADE, instante)
}

/** O usuario clicou em "Continuar conectado" no dialogo de aviso. */
export function continuarConectado() {
    estadoSessao.avisando = false
    ultimoRegistro = 0
    registrarAtividade()
    avaliar()
}

export function encerrarSessao(motivo) {
    pararRelogio()

    try {
        const chaves = [
            'token', 'tokenAtualizacao', 'tokenExpiration',
            'user', 'userData', 'permissoes', 'id', 'idUsuario', 'userLogin',
            'participanteAtivo',
            CHAVE_ULTIMA_ATIVIDADE, CHAVE_INICIO
        ]
        chaves.forEach(function (chave) { localStorage.removeItem(chave) })
        sessionStorage.removeItem('modais_exibidos')

        // sessionStorage e nao localStorage: a lista acima acabou de ser
        // limpa, e o motivo precisa sobreviver ate a tela de login ler.
        if (motivo) sessionStorage.setItem(CHAVE_MOTIVO_ENCERRAMENTO, motivo)
    } catch (e) {
        // storage indisponivel - segue para o reload de qualquer forma.
    }

    try {
        window.location.hash = '#/login'
        window.location.reload()
    } catch (e) {
        // ambiente sem window.
    }
}

function renovar() {
    if (renovacaoEmVoo) return
    renovacaoEmVoo = true

    LoginService.renovarToken()
        .then(function (resposta) {
            const dados = resposta && resposta.data ? resposta.data : {}
            const novoToken = dados.tokenAcesso || dados.token

            if (novoToken && typeof novoToken === 'string' && novoToken.length > 10) {
                localStorage.setItem('token', novoToken)
                if (dados.tokenAtualizacao) {
                    localStorage.setItem('tokenAtualizacao', dados.tokenAtualizacao)
                }
            }
        })
        .catch(function () {
            // Sem estender o token, o proximo 401 cai no handle401 do http.js.
            // Nao encerramos aqui: o usuario pode estar no meio de algo e o
            // refresh pode voltar a funcionar no proximo ciclo.
        })
        .finally(function () {
            renovacaoEmVoo = false
        })
}

/** Um ciclo de decisao. Exportado para o teste conseguir chamar sem relogio. */
export function avaliar(agora) {
    if (!temSessao()) return { acao: 'nada' }

    const instante = agora || Date.now()
    const decisao = decidir({
        agora: instante,
        ultimaAtividade: ler(CHAVE_ULTIMA_ATIVIDADE),
        inicioSessao: ler(CHAVE_INICIO),
        expiracaoToken: expiracaoDoToken(localStorage.getItem('token'))
    })

    estadoSessao.restanteOciosidadeMs = decisao.restanteOciosidade || 0

    if (decisao.acao === 'encerrar') {
        encerrarSessao(decisao.motivo)
    } else if (decisao.acao === 'avisar') {
        estadoSessao.avisando = true
        estadoSessao.motivo = decisao.motivo
        estadoSessao.segundos = decisao.segundos
    } else {
        estadoSessao.avisando = false
        estadoSessao.motivo = null
        if (decisao.acao === 'renovar') renovar()
    }

    return decisao
}

function aoInteragir() {
    registrarAtividade()
}

function aoVoltarParaAba() {
    // Aba oculta tem o timer estrangulado pelo navegador, e o notebook pode ter
    // sido suspenso. Como toda conta e por timestamp absoluto, basta reavaliar
    // ao voltar - nao ha contador acumulado para corrigir.
    if (!document.hidden) avaliar()
}

function aoMudarStorage(evento) {
    // Atividade em OUTRA aba. Sem isto, a aba ociosa derrubaria a sessao de
    // quem esta trabalhando na aba do lado, porque o localStorage e comum.
    if (evento && evento.key === CHAVE_ULTIMA_ATIVIDADE) avaliar()
}

function pararRelogio() {
    if (intervalo) {
        clearInterval(intervalo)
        intervalo = null
    }
}

export function iniciar() {
    parar()

    EVENTOS_DE_ATIVIDADE.forEach(function (evento) {
        window.addEventListener(evento, aoInteragir, { passive: true })
    })
    document.addEventListener('visibilitychange', aoVoltarParaAba)
    window.addEventListener('storage', aoMudarStorage)

    // Sessao que ja existia antes desta versao nao tem os carimbos novos.
    // Sem isto, o primeiro tick encerraria a sessao de quem esta logado no
    // momento do deploy.
    if (temSessao() && (!ler(CHAVE_ULTIMA_ATIVIDADE) || !ler(CHAVE_INICIO))) {
        iniciarContagemDaSessao()
    }

    intervalo = setInterval(function () { avaliar() }, INTERVALO_TICK_MS)
    avaliar()
}

export function parar() {
    pararRelogio()

    EVENTOS_DE_ATIVIDADE.forEach(function (evento) {
        window.removeEventListener(evento, aoInteragir)
    })
    document.removeEventListener('visibilitychange', aoVoltarParaAba)
    window.removeEventListener('storage', aoMudarStorage)
}

export { AVISO_ANTES_MS }
